import * as _ from 'lodash';
import { indexBy, map, pipe } from 'remeda';

import { BankAccountWithInstitutionAndOwnerFragmentGQLType } from '../../../../../../types/dashboard-admin-gqlTypes.generated.d';
import {
  LoanProductGQLType,
  OrganizationGQLType,
  UserGQLType,
} from '../../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  CreditAgreement,
  LoanProductsMap,
  OrganizationIdToBankAccountsMap,
  OrganizationIdToUsersMap,
  OrganizationsWithUsers,
} from 'packages/creditAgreements/ui/types';

export const getOrganizationIds = (
  creditAgreementFullArtifact: CreditAgreement.DomainEntity.CreditAgreementFullArtifact,
) => {
  const partyOrganizationIds = _.flatMap(
    _.values(creditAgreementFullArtifact.facilityMechanics.parties),
    (parties) =>
      parties.map((partyItem) => {
        return partyItem.entity.id;
      }),
  );
  return _.uniq(partyOrganizationIds);
};

export const createOrganizationMap = (
  organizations: Pick<OrganizationGQLType, 'id' | 'name' | 'slug'>[],
): {
  [key: string]: CreditAgreement.DomainEntity.Items.BaseOrganizationEntity;
} => {
  const orgEntities: CreditAgreement.DomainEntity.Items.BaseOrganizationEntity[] = _.map(
    organizations,
    (org) => {
      return {
        entity: {
          id: org.id,
          slug: org.slug,
          name: org.name,
        },
        users: [],
        _isWrittenIn_: true,
      };
    },
  );

  return _.keyBy(orgEntities, (org) => org.entity.id);
};

export const createOrganizationIdToUserMap = (
  organizationsWithUsers: OrganizationsWithUsers[],
): OrganizationIdToUsersMap => {
  return _.reduce<
    Pick<OrganizationGQLType, 'id' | 'slug' | 'name'> & {
      users: Pick<UserGQLType, 'id' | 'firstName' | 'lastName' | 'email'>[];
    },
    OrganizationIdToUsersMap
  >(
    organizationsWithUsers,
    (acc, orgWithUsers) => {
      return {
        ...acc,
        [orgWithUsers.id]: _.map(orgWithUsers.users, (user) => ({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
        })),
      };
    },
    {},
  );
};

export const createOrganizationIdToBankAccountsMap = (
  bankAccountsWithInstitution: BankAccountWithInstitutionAndOwnerFragmentGQLType[],
): OrganizationIdToBankAccountsMap => {
  return _.groupBy(bankAccountsWithInstitution, 'organizationId');
};

export const createLoanProductsMap = (
  loanProducts: LoanProductGQLType[],
): LoanProductsMap => {
  return _.keyBy(loanProducts, 'id');
};

export const createCapitalProvidersMap = (
  capitalProviders: CreditAgreement.DomainEntity.Items.BaseOrganizationEntity[],
) =>
  pipe(
    capitalProviders,
    map((cp) => cp.entity),
    indexBy((entity) => entity.id),
  );

export const createBankAccountsMap = (
  parties: CreditAgreement.DomainEntity.PartyEntities,
): Map<string, CreditAgreement.DomainEntity.Items.BankAccountEntity> => {
  const bankAccountMap = new Map(
    [...parties.borrowers, ...parties.capitalProviders]
      .flatMap((entity) => entity.bankAccounts)
      .map((account) => [account.entity.id, account]),
  );

  return bankAccountMap;
};
